import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AlertController, LoadingController, NavController } from "@ionic/angular";
// import { Storage } from "@ionic/storage-angular";
import { BehaviorSubject, Subject, Subscription } from "rxjs";
import { SystemService } from "./system.service";
import { API } from "../_constants/constants";
import { Storage } from "@ionic/storage-angular";
import bcrypt from "bcryptjs";
import { BadgeService, BleConnection } from "./badge.service";

/**
 * Interface describing the user object returned by your HackathonRoutes.
 * Matches the JSON shape in /hackathon/login or /hackathon/signup responses.
 */
export interface AuthHackathonUser extends HackathonUser {
    password: string; // hashed
    passwordConfirm: string; // hashed
}
export interface HackathonUser {
    Id: string;
    CreatedAt: string;
    UpdatedAt: string;
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    title: string;
    bio: string;
    pfp: string;
    uuid: string;
}
export interface MatrixUser {
    access_token: string;
    user_id: string;
    home_server: string;
    device_id: string;
}

/**
 * Shape of your /login or /signup API responses
 */
export interface AppAuth {
    token: string;
    user: AuthHackathonUser;
    matrix: MatrixUser;
}

@Injectable({
    providedIn: "root"
})
export class AuthService {
    private currentAuthSubject = new BehaviorSubject<AppAuth | null>(null);
    public currentAuth$ = this.currentAuthSubject.asObservable();

    authHeaders?: HttpHeaders

	profiles: Map<string, HackathonUser> = new Map();

    /**
     * Keys for storing data in Ionic Storage
     */
    // private TOKEN_KEY = "hackathon-token";
    // private USER_KEY = "hackathon-user";
    // private MATRIX_USER_KEY = "matrix-user";
    private AUTH_STORAGE_KEY = "hackathon-auth";
    private BADGE_STORAGE_KEY = "hackathon-badge";

    badgeConnection : Subscription

    get authorized() {
        return true;
        // TODO: verify auth token stored?
    }

    get appAuth() {
        return this.currentAuthSubject.value;
    }

    constructor(
        private http: HttpClient,
        public storage: Storage,
        private alertCtrl: AlertController,
        private navCtrl: NavController,
        private loadingCtrl: LoadingController,
        private systemService: SystemService,
        private badge: BadgeService,
    ) {
        
        this.initStorage();

        this.badgeConnection = this.badge.connected$.subscribe((connection) => {
            if (connection) this.storage.set(this.BADGE_STORAGE_KEY, connection)
            else console.log("Disconnected from badge");
        })
    }

    /**
     * Initialize Ionic Storage and check if a user/token is already saved.
     */
    private async initStorage() {
        await this.storage.create();
        const storedAuth : AppAuth = await this.storage.get(this.AUTH_STORAGE_KEY);
        const storedBadge : BleConnection = await this.storage.get(this.BADGE_STORAGE_KEY);
        console.log("Stored auth:", storedAuth);
        
        if (storedAuth) {
            this.setAuth(storedAuth, true);

            if (storedBadge) {
                console.log("Stored badge, connecting:", storedBadge);
                await this.badge.connectToDevice(storedBadge.device);
            }
        }
        else {
            // TODO: Return to login/signup?
        }
    }

    async setAuth(appAuth : AppAuth, dontStore?: boolean) {
        if (!dontStore) this.storage.set(this.AUTH_STORAGE_KEY, appAuth);
        await this.setAuthHeader(appAuth.user.uuid, appAuth.token)
        this.currentAuthSubject.next(appAuth);
        this.setProfiles()
        
        // Prompt badge connection if not connected
        if (!this.badge.connectedDevice) this.badge.openModal()
    }

    setAuthHeader(uuid: string, token: string) {
        this.authHeaders = new HttpHeaders ({
            'Accept': '*/*',
            'Access-Control-Allow-Origin': '*',
            'uuid': uuid,
            'token': token
        }) 
        return this.authHeaders
    }

    async setProfiles() {
        const profiles = await this.searchProfile()
        profiles.forEach(profile => {
            this.profiles.set(profile.uuid, profile)
        })
    }


    get authHeader() : { headers: HttpHeaders } | undefined {
        if (!this.user || !this.token) return undefined
        let headers = this.authHeaders
        if (!headers) headers = this.setAuthHeader(this.user.uuid, this.token)
        return { headers }
    }

    get user(): AuthHackathonUser | null {
        return this.currentAuthSubject?.value?.user;
    }
    get matrix(): MatrixUser | null {
        return this.currentAuthSubject?.value?.matrix;
    }
    get token(): string | null {
        return this.currentAuthSubject?.value?.token;
    }

    /**
     * Sign up a new user
     * POST /hackathon/signup
     * Accepts { email, password, firstName, lastName, company, title, pfp }
     * Returns { token, user }
     */
    async signup(formData: Partial<AuthHackathonUser>): Promise<AppAuth> {
        const loading = await this.loadingCtrl.create({ message: "Signing up..." });
        await loading.present();

        try {
            const appAuth = await this.http.post<AppAuth>(`${API}/signup`, formData).toPromise();
            await loading.dismiss();
            if (!appAuth) throw new Error("No response from /signup");

            this.setAuth(appAuth);

            return appAuth;
        } catch (err) {
            await loading.dismiss();
            console.error("Signup error:", err);
            throw err;
        }
    }

    /**
     * Login existing user
     * POST /hackathon/login
     * Accepts { email, rawPassword }
     * Returns { token, user }
     */
    async login(email: string, rawPassword: string): Promise<AppAuth> {
        const loading = await this.loadingCtrl.create({ message: "Logging in..." });
        await loading.present();

        // const password = await this.hashPassword(rawPassword);

        try {
            const body = { email, password: rawPassword };
            const appAuth = await this.http.post<AppAuth>(`${API}/login`, body).toPromise();

            await loading.dismiss();
            if (!appAuth) throw new Error("No response from /login");

            this.setAuth(appAuth);

            return appAuth;
        } catch (err) {
            await loading.dismiss();
            console.error("Login error:", err);
            throw err;
        }
    }

    /**
     * Logs out user (clears token & user from storage, resets BehaviorSubject)
     */
    async logout(): Promise<void> {
        const alert = await this.alertCtrl.create({
            header: "Confirm Logout",
            message: "Are you sure you want to log out?",
            buttons: [
                {
                    text: "Logout",
                    handler: async () => {
                        await this.logoutHard();
                    }
                },
                {
                    text: "Cancel",
                    role: "cancel"
                }
            ]
        });
        await alert.present();
    }

    /**
     * Actually removes token & user, resets current user, optionally navigates
     */
    private async logoutHard() {
        // this.emit("logout");
        await this.storage.remove(this.AUTH_STORAGE_KEY);
        await this.storage.remove(this.BADGE_STORAGE_KEY);
        this.currentAuthSubject.next(null);

        // e.g., navigate to some "home" or "default" page
        this.navCtrl.navigateRoot(["/"]);
    }

    /**
     * Example: fetch profile from /hackathon/profile (protected by JWT)
     */
    async getProfile(uuid?: string | number): Promise<HackathonUser> {
        if (!uuid) uuid = this.user?.uuid;
        const user = await this.http.get<HackathonUser>(`${API}/profile/${uuid}`, this.authHeader).toPromise();
        if (!user) throw new Error("No user returned from /profile");
        return user as any;
    }

    /**
     * If you have a route to update user data, e.g. /hackathon/update
     */
    async updateProfile(data: Partial<HackathonUser>): Promise<any> {
        data.uuid = this.user?.uuid;
        data.Id = this.user?.Id;
        const res = await this.http.post(`${API}/update`, data, this.authHeader).toPromise();
        this.setProfiles()
        return res
    }

    async deleteProfile() {
        console.log("Deleting profile...");
        
        const alert = await this.alertCtrl.create({
            header: "Confirm Delete",
            message: "Are you sure you want to delete your profile? You will be able to register a new account with this email address, but you will lose access to previous messages and data. Consider editing your profile instead.",
            buttons: [
                {
                    text: "Delete",
                    handler: async () => {
                        await alert.present();
                        const data = { uuid: this.user?.uuid, Id: this.user?.Id };
                        const res = await this.http.post(`${API}/destroy-me`, data, this.authHeader).toPromise();
                        this.logoutHard()
                        return res
                    }
                },
                {
                    text: "Nevermind",
                    role: "cancel"
                }
            ]
        });
        await alert.present();
    }

    async searchProfile(query?: string): Promise<HackathonUser[]> {
        const users = await this.http.get<HackathonUser[]>(`${API}/search/${query}`, this.authHeader).toPromise();
        // console.log("Search results:", users);
        return users
    }

    support() {
        // TODO: open direct message with support constant
    }

    async hashPassword(rawPassword: string){
        const salt = await bcrypt.genSalt(10);
        const hashedPassword = await bcrypt.hash(rawPassword, salt);
        return hashedPassword;
    }
}
